import { SequentialPagination } from '@/components/common/paging';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import type { ActionKey, ColumnInfo, ColumnKey } from './columns';
import { CellDisplay } from './columns';
import { createColumn, makeColumnLookup } from './columns';
import { useEffect, useMemo, useState } from 'react';
import type { User } from '@/shared/types/user';

const itemsPerPage = 10;

export function UsersTable({
  items,
  count,
  onAction,
}: {
  items: User[];
  count: number;
  onAction: (item: User, action: ActionKey) => void;
}) {
  const cols: ColumnInfo[] = useMemo(
    () => [
      createColumn({ key: 'displayName', displayName: 'Name' }),
      createColumn({ key: 'email' }),
      createColumn({ key: 'access', displayName: 'Access', align: 'center' }),
      createColumn({ key: 'actions', displayName: 'Actions', align: 'center' }),
    ],
    [],
  );
  const columnLookup = useMemo(() => makeColumnLookup(cols), [cols]);

  const [page, setPage] = useState(0);
  const visibleItems = useMemo(() => {
    return items.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [items, page]);
  useEffect(() => {
    setPage(1);
  }, [items]);

  return (
    <Table
      aria-label='Users'
      bottomContent={
        <div className='flex w-full justify-center'>
          <SequentialPagination
            page={page}
            itemsPerPage={itemsPerPage}
            count={count}
            onPageChange={setPage}
          />
        </div>
      }
    >
      <TableHeader columns={cols}>
        {(col) => (
          <TableColumn
            key={col.key}
            className={col.align ? `text-${col.align}` : ''}
          >
            {col.displayName}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody items={visibleItems}>
        {(item) => (
          <TableRow key={item.id}>
            {(col) => {
              const column = columnLookup[col as ColumnKey];
              const className = column.align ? `text-${column.align}` : '';

              return (
                <TableCell key={col} className={className}>
                  <CellDisplay item={item} col={column} onAction={onAction} />
                </TableCell>
              );
            }}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
